import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { TreeNodeIndent } from "./TreeNodeIndent";
import { renameObjectKey } from "./Helpers";
import { TreeContext, TreeWriteContext } from "./TreeContext";
import { useTreeDataContext, useTreeModifyContext } from "./TreeDataContext";
import { RawJsonEditor } from "./RawJsonEditor";
import BuildIcon from "@mui/icons-material/Build";
import { Box, Button, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

export function TreeView(props: PropsWithChildren) {
  const { treeData, setTreeData, getAllComponentNames } = useTreeDataContext();
  const { addGamePiece } = useTreeModifyContext();

  const [path] = useState<string[]>([]);
  const [isRaw, setIsRaw] = useState(false);
  const [newGamePieceName, setNewGamePieceName] = useState("");

  const handleOnTryRenameGamePiece = useCallback(
    (oldName: string, newName: string) => {
      const componentNames = getAllComponentNames();
      const isNewNameValid = IsGamePieceNameIsValid(newName, componentNames);
      if (!isNewNameValid) {
        // If the new name is not valid, do nothing
        return;
      }

      setTreeData((oldTreeData: any) => {
        let modifiedTreeData = JSON.parse(JSON.stringify(oldTreeData));
        modifiedTreeData = renameObjectKey(modifiedTreeData, oldName, newName);
        delete modifiedTreeData[oldName];
        replaceNameInAcceptsArray(modifiedTreeData, oldName, newName);
        return modifiedTreeData;
      });
    },
    [getAllComponentNames, setTreeData],
  );

  const treeWriteContext = useMemo(
    () => ({
      onTryRenameGamePiece: handleOnTryRenameGamePiece,
    }),
    [handleOnTryRenameGamePiece],
  );

  const treeGraph = useMemo(() => {
    return <TreeNodeIndent depth={0} data={treeData} path={path} />;
  }, [treeData, path]);

  const treeContext = useMemo(
    () => ({
      checkIfGamePieceNameIsValid: (name: string) => IsGamePieceNameIsValid(name, getAllComponentNames()),
    }),
    [getAllComponentNames],
  );

  const canAddGamePiece = useMemo(() => {
    return IsGamePieceNameIsValid(newGamePieceName, getAllComponentNames());
  }, [getAllComponentNames, newGamePieceName]);

  if (treeData === undefined) {
    return <Typography>No tree data</Typography>;
  }

  return (
    <TreeContext.Provider value={treeContext}>
      <TreeWriteContext.Provider value={treeWriteContext}>
        <Box className="tree">
          <Box className="horizontal-flex flex-wrap tree-controls">
            <TextField
              id="new-game-piece-name"
              label="New game piece name"
              variant="outlined"
              value={newGamePieceName}
              onChange={(event) => setNewGamePieceName(event.target.value)}
            />
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              color="success"
              disabled={!canAddGamePiece}
              aria-label="add game piece"
              onClick={() => {
                addGamePiece(newGamePieceName, { width: 100, height: 100 });
                setNewGamePieceName("");
              }}
            >
              Add game piece
            </Button>
            <Button
              startIcon={<BuildIcon />}
              variant="contained"
              color="secondary"
              aria-label="raw editor"
              onClick={() => setIsRaw(!isRaw)}
            >
              {!isRaw ? "Edit raw data" : "Stop editing raw data"}
            </Button>
          </Box>

          {!isRaw && <Box className="tree-graph">{treeGraph}</Box>}
          {isRaw && <RawJsonEditor rawValue={JSON.stringify(treeData, null, 2)} path={[]} gamePieceName={null} />}
          {props.children}
        </Box>
      </TreeWriteContext.Provider>
    </TreeContext.Provider>
  );
}

function replaceNameInAcceptsArray(treeData: any, oldName: string, newName: string) {
  for (let key in treeData) {
    let value = treeData[key];
    if (Array.isArray(value) && (key === "accepts" || key === "__cond_accepts")) {
      for (let i = 0; i < value.length; i++) {
        if (value[i] === oldName) {
          value[i] = newName;
        }
      }
    } else if (typeof value === "object" && value !== null) {
      replaceNameInAcceptsArray(value, oldName, newName);
    }
  }
}

const keywords = ["condition", "root", "result", "accepts", "width", "height", "positions", "object"];

function IsGamePieceNameIsValid(gamePieceName: string, allComponentNames: string[]) {
  if (gamePieceName.length < 3 || gamePieceName.startsWith("__") || allComponentNames.includes(gamePieceName)) {
    return false;
  }
  if (keywords.includes(gamePieceName)) {
    return false;
  }
  return true;
}
